import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Divider,
  Chip,
  TextField,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Image as ImageIcon,
  TextFields as TextFieldsIcon,
  Height as HeightIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';

interface LoginSettings {
  logoPath: string; // legacy
  loginImagePath: string;
  topLogoPath?: string;
  bottomLogoPath?: string;
  loginText?: string;
  topLogoHeight?: number;
  updatedAt?: Date;
}

const LoginPageSettings: React.FC = () => {
  const [settings, setSettings] = useState<LoginSettings>({
    logoPath: '',
    loginImagePath: '',
    topLogoPath: '',
    bottomLogoPath: '',
    loginText: '',
    topLogoHeight: 100
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  // Hämta inställningar vid laddning
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await axiosInstance.get('/api/login-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching login settings:', error);
      setMessage({ type: 'error', text: 'Kunde inte ladda inloggningsinställningar' });
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleTopLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      setMessage(null);
      await axiosInstance.post('/api/login-settings/logo/top', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setMessage({ type: 'success', text: 'Övre logotype uppdaterad!' });
      try { window.location.hash = 'login-page'; } catch {}
      await fetchSettings();
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Kunde inte ladda upp övre logotype' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBottomLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      setMessage(null);
      await axiosInstance.post('/api/login-settings/logo/bottom', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setMessage({ type: 'success', text: 'Nedre logotype uppdaterad!' });
      try { window.location.hash = 'login-page'; } catch {}
      await fetchSettings();
    } catch (error: any) {
      console.error('Error uploading bottom logo:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Kunde inte ladda upp nedre logotype' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      setMessage(null);
      await axiosInstance.post('/api/login-settings/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setMessage({ type: 'success', text: 'Bakgrundsbild uppdaterad!' });
      try { window.location.hash = 'login-page'; } catch {}
      await fetchSettings();
    } catch (error: any) {
      console.error('Error uploading image:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Kunde inte ladda upp bild' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (loadingSettings) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1400px' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#072f53', fontWeight: 700 }}>
          Inloggningssida
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Anpassa utseendet på din inloggningssida med egna logotyper, text och bakgrundsbild.
        </Typography>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }} 
          onClose={() => setMessage(null)}
          icon={message.type === 'success' ? <CheckCircleIcon /> : undefined}
        >
          {message.text}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Övre logotype */}
        <Card elevation={2}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <UploadIcon sx={{ mr: 1, color: '#072f53' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#072f53' }}>
                Övre logotyp
              </Typography>
              <Chip label="Rekommenderad höjd: 80-200px" size="small" sx={{ ml: 2 }} />
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Denna logotyp visas högst upp på inloggningssidan mot en blå bakgrund (#072f53). Använd helst en vit eller ljus logotyp för bästa kontrast.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'start' }}>
              {/* Preview */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Förhandsvisning:
                </Typography>
                {(settings.topLogoPath || settings.logoPath) ? (
                  <Paper sx={{ p: 3, bgcolor: '#072f53', display: 'inline-block', borderRadius: 2 }}>
                    <img 
                      src={`${(settings.topLogoPath || settings.logoPath) + '?t=' + Date.now()}`} 
                      alt="Login Logo" 
                      style={{ height: `${settings.topLogoHeight || 100}px`, width: 'auto', display: 'block', maxWidth: '500px' }}
                    />
                  </Paper>
                ) : (
                  <Paper sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2, minWidth: 200, minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Ingen logotyp uppladdad</Typography>
                  </Paper>
                )}
              </Box>
              
              {/* Controls */}
              <Box sx={{ flex: 1 }}>
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={loading}
                    sx={{ alignSelf: 'start' }}
                  >
                    Ladda upp logotyp
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleTopLogoUpload}
                    />
                  </Button>

                  <Divider />

                  {/* Height control */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <HeightIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Justera höjd
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <TextField
                        type="number"
                        size="small"
                        value={settings.topLogoHeight || 100}
                        onChange={(e) => setSettings(prev => ({ ...prev, topLogoHeight: Number(e.target.value) || 100 }))}
                        inputProps={{ min: 40, max: 600 }}
                        sx={{ width: 120 }}
                        label="Höjd (px)"
                      />
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={async () => {
                          try {
                            setLoading(true); 
                            setMessage(null);
                            await axiosInstance.put('/api/login-settings/top-logo-height', { height: settings.topLogoHeight || 100 });
                            setMessage({ type: 'success', text: 'Logotypens höjd uppdaterad!' });
                            await fetchSettings();
                          } catch (err: any) {
                            setMessage({ type: 'error', text: err.response?.data?.message || 'Kunde inte spara höjd' });
                          } finally { 
                            setLoading(false); 
                          }
                        }}
                        disabled={loading}
                      >
                        Spara höjd
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Min: 40px, Max: 600px
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Informationstext */}
        <Card elevation={2}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextFieldsIcon sx={{ mr: 1, color: '#072f53' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#072f53' }}>
                Informationstext
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Lägg till en välkomsttext eller information som visas under den övre logotypen. Du kan formatera texten med verktygsfältet nedan.
            </Typography>
            
            {/* Rich text toolbar */}
            <Paper sx={{ p: 1, mb: 2, bgcolor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Formatering:</Typography>
                <Button size="small" variant="outlined" onClick={() => document.execCommand('bold')} sx={{ minWidth: 40 }}>
                  <strong>B</strong>
                </Button>
                <Button size="small" variant="outlined" onClick={() => document.execCommand('italic')} sx={{ minWidth: 40 }}>
                  <em>I</em>
                </Button>
                <Button size="small" variant="outlined" onClick={() => document.execCommand('underline')} sx={{ minWidth: 40 }}>
                  <u>U</u>
                </Button>
                <Divider orientation="vertical" flexItem />
                <Button size="small" variant="outlined" onClick={() => document.execCommand('formatBlock', false, 'h2')}>H2</Button>
                <Button size="small" variant="outlined" onClick={() => document.execCommand('formatBlock', false, 'h3')}>H3</Button>
                <Button size="small" variant="outlined" onClick={() => document.execCommand('formatBlock', false, 'p')}>P</Button>
                <Divider orientation="vertical" flexItem />
                <Button size="small" variant="outlined" onClick={() => document.execCommand('insertUnorderedList')}>• Lista</Button>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">Färg:</Typography>
                  <input 
                    type="color" 
                    onChange={(e) => document.execCommand('foreColor', false, e.target.value)} 
                    title="Textfärg"
                    style={{ cursor: 'pointer', height: 32, width: 50 }}
                  />
                </Box>
              </Box>
            </Paper>
            
            <Box sx={{ mb: 2, border: '2px solid #e0e0e0', borderRadius: 2, backgroundColor: 'white', p: 2, minHeight: 150 }}>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={() => setSettings(prev => ({ ...prev, loginText: editorRef.current?.innerHTML || '' }))}
                style={{ outline: 'none', minHeight: 120 }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(settings.loginText || '') }}
              />
            </Box>
            
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  setLoading(true);
                  setMessage(null);
                  await axiosInstance.put('/api/login-settings/text', { loginText: (editorRef.current?.innerHTML || settings.loginText || '') });
                  setMessage({ type: 'success', text: 'Text uppdaterad!' });
                } catch (err: any) {
                  console.error('Error saving text:', err);
                  setMessage({ type: 'error', text: err.response?.data?.message || 'Kunde inte spara text' });
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
            >
              Spara text
            </Button>
          </CardContent>
        </Card>

        {/* Bakgrundsbild och Nedre logotyp - side by side */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Bakgrundsbild */}
          <Card elevation={2}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ImageIcon sx={{ mr: 1, color: '#072f53' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#072f53' }}>
                  Bakgrundsbild
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Bilden visas till höger om inloggningsformuläret på större skärmar.
              </Typography>
              
              {settings.loginImagePath ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Förhandsvisning:
                  </Typography>
                  <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 2, display: 'inline-block' }}>
                    <img 
                      src={`${settings.loginImagePath}?t=${Date.now()}`} 
                      alt="Login Background" 
                      style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'cover', display: 'block' }}
                    />
                  </Paper>
                </Box>
              ) : (
                <Paper sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2, mb: 2, minHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Ingen bild uppladdad</Typography>
                </Paper>
              )}
              
              <Button
                variant="contained"
                component="label"
                startIcon={<ImageIcon />}
                disabled={loading}
              >
                Ladda upp bild
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>
            </CardContent>
          </Card>

          {/* Nedre logotype */}
          <Card elevation={2}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <UploadIcon sx={{ mr: 1, color: '#072f53' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#072f53' }}>
                  Nedre logotyp
                </Typography>
                <Chip label="Max höjd: 60px" size="small" sx={{ ml: 2 }} />
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Denna logotyp visas längst ned på inloggningssidan.
              </Typography>
              
              {settings.bottomLogoPath ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Förhandsvisning:
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#072f53', display: 'inline-block', borderRadius: 2 }}>
                    <img 
                      src={`${settings.bottomLogoPath}?t=${Date.now()}`} 
                      alt="Bottom Logo" 
                      style={{ maxHeight: '60px', display: 'block' }}
                    />
                  </Paper>
                </Box>
              ) : (
                <Paper sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2, mb: 2, minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Ingen logotyp uppladdad</Typography>
                </Paper>
              )}
              
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadIcon />}
                disabled={loading}
              >
                Ladda upp logotyp
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleBottomLogoUpload}
                />
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Stack>
    </Box>
  );
};

export default LoginPageSettings;

